import { useState, useCallback } from 'react';

export type InsightCategory = 'reflection' | 'action' | 'learning' | 'pattern';

export interface Insight {
  id: number;
  user_id: number;
  session_id: string | null;
  session_title: string | null;
  session_summary: string | null;
  title: string;
  content: string;
  category: InsightCategory;
  is_pinned: boolean | number;
  is_archived: boolean | number;
  created_at: string;
  updated_at: string;
}

interface UseInsightsOptions {
  apiUrl?: string;
  userId?: string | number;
}

export const useInsights = ({ apiUrl = '/api', userId }: UseInsightsOptions) => {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInsights = useCallback(async () => {
    if (!userId) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/chat/insights/${userId}?limit=200`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch insights');
      const data = await response.json();
      setInsights(data.data || []);
    } catch (e: any) {
      console.error('[useInsights] fetch error:', e);
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl, userId]);

  const generateFromSession = useCallback(
    async (sessionId: string): Promise<Insight[]> => {
      if (!userId || !sessionId) return [];
      setIsGenerating(true);
      setError(null);
      try {
        const response = await fetch(`${apiUrl}/chat/insights/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ userId, sessionId }),
        });
        if (!response.ok) throw new Error('Failed to generate insights');
        const data = await response.json();
        const generated: Insight[] = data.data?.insights || [];

        // Prepend new insights to local state
        if (generated.length > 0) {
          setInsights((prev) => [...generated, ...prev]);
        }

        return generated;
      } catch (e: any) {
        console.error('[useInsights] generate error:', e);
        setError(e.message);
        return [];
      } finally {
        setIsGenerating(false);
      }
    },
    [apiUrl, userId]
  );

  const togglePin = useCallback(
    async (insightId: number, currentPinned: boolean) => {
      try {
        const response = await fetch(`${apiUrl}/chat/insights/${insightId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ is_pinned: !currentPinned }),
        });
        if (!response.ok) throw new Error('Failed to update');

        // Optimistic update
        setInsights((prev) =>
          prev
            .map((i) => (i.id === insightId ? { ...i, is_pinned: !currentPinned } : i))
            .sort((a, b) => {
              const pa = a.is_pinned ? 1 : 0;
              const pb = b.is_pinned ? 1 : 0;
              if (pa !== pb) return pb - pa;
              return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            })
        );
      } catch (e: any) {
        console.error('[useInsights] toggle pin error:', e);
      }
    },
    [apiUrl]
  );

  const removeInsight = useCallback(
    async (insightId: number) => {
      try {
        const response = await fetch(`${apiUrl}/chat/insights/${insightId}`, {
          method: 'DELETE',
          credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to delete');
        setInsights((prev) => prev.filter((i) => i.id !== insightId));
      } catch (e: any) {
        console.error('[useInsights] delete error:', e);
      }
    },
    [apiUrl]
  );

  return {
    insights,
    isLoading,
    isGenerating,
    error,
    fetchInsights,
    generateFromSession,
    togglePin,
    removeInsight,
  };
};
